/**
 * usePolling Hook
 * Smart polling mechanism with exponential backoff and Page Visibility API integration
 */

import { useEffect, useRef } from 'react';

interface UsePollingOptions<T> {
  enabled?: boolean;
  immediate?: boolean;
  maxInterval?: number;
  onDataChange?: (data: T) => void;
  onError?: (error: Error) => void;
}

// Deep comparison utility
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(objA[key], objB[key])) return false;
  }

  return true;
}

/**
 * Custom hook for polling with smart features:
 * - Exponential backoff on errors (2x, 4x, 8x, capped at maxInterval)
 * - Pause polling when tab is hidden (Page Visibility API)
 * - Deep comparison to only trigger updates when data actually changes
 * - Cleanup on unmount
 * 
 * @param pollingFn - Async function to call at intervals
 * @param interval - Base polling interval in milliseconds
 * @param options - Configuration options
 */
export function usePolling<T = unknown>(
  pollingFn: () => Promise<T>,
  interval: number,
  options: UsePollingOptions<T> = {}
) {
  const {
    enabled = true,
    immediate = true,
    maxInterval = 60000, // 60 seconds max
    onDataChange,
    onError,
  } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const errorCountRef = useRef<number>(0);
  const previousDataRef = useRef<T | null>(null);
  const isVisibleRef = useRef<boolean>(!document.hidden);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const executePoll = async () => {
      if (!isVisibleRef.current) return;

      try {
        const data = await pollingFn();

        // Reset error count on success
        const hadErrors = errorCountRef.current > 0;
        errorCountRef.current = 0;

        // Check if data has changed
        if (!deepEqual(previousDataRef.current, data)) {
          previousDataRef.current = data;
          if (onDataChange) {
            onDataChange(data);
          }
        }

        // Restart interval with normal interval if we had errors before
        if (hadErrors && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(executePoll, interval);
        }
      } catch (error) {
        // Increment error count and apply exponential backoff
        errorCountRef.current += 1;
        const backoffMultiplier = Math.pow(2, errorCountRef.current);
        const newInterval = Math.min(
          interval * backoffMultiplier,
          maxInterval
        );

        if (onError && error instanceof Error) {
          onError(error);
        }

        // Restart interval with new backoff time
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(executePoll, newInterval);
        }
      }
    };

    const handleVisibilityChange = () => {
      const wasVisible = isVisibleRef.current;
      isVisibleRef.current = !document.hidden;

      if (!wasVisible && isVisibleRef.current) {
        // Tab became visible - restart polling
        const currentInterval = errorCountRef.current > 0
          ? Math.min(interval * Math.pow(2, errorCountRef.current), maxInterval)
          : interval;
        
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(executePoll, currentInterval);
      } else if (wasVisible && !isVisibleRef.current) {
        // Tab became hidden - stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Execute immediately if immediate option is true
    if (immediate) {
      executePoll();
    }

    // Start interval polling only if tab is visible
    if (isVisibleRef.current) {
      intervalRef.current = setInterval(executePoll, interval);
    }

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, immediate, interval, maxInterval, pollingFn, onDataChange, onError]);
}
